/**
 * Action Item Card Component
 * Card display in kanban column
 */
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { formatDistanceToNow } from 'date-fns'
import { MessageCircle, Link as LinkIcon, User, GripVertical } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { ActionItem, ActionPriority } from '@/types/actions'
import { PRIORITY_COLORS, PRIORITY_LABELS } from '@/types/actions'

interface ActionItemCardProps {
  item: ActionItem
  onClick: () => void
}

const PRIORITY_BORDER_CLASS: Record<ActionPriority, string> = {
  low: 'border-l-gray-400',
  medium: 'border-l-amber-500',
  high: 'border-l-red-500',
}

export function ActionItemCard({ item, onClick }: ActionItemCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: {
      item,
      status: item.status,
    },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`relative hover:shadow-md transition-all border-l-4 ${PRIORITY_BORDER_CLASS[item.priority]} ${
        isDragging ? 'shadow-lg ring-2 ring-primary' : ''
      }`}
    >
      <div className="flex gap-2">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 pt-4 pl-2 cursor-grab active:cursor-grabbing hover:text-primary"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Card Content - Clickable */}
        <div className="flex-1 p-4 pl-0 cursor-pointer" onClick={onClick}>
          {/* Priority Badge & Title */}
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm line-clamp-2 flex-1">{item.title}</h4>
          <Badge
            variant="outline"
            className="text-xs"
            style={{
              borderColor: PRIORITY_COLORS[item.priority],
              color: PRIORITY_COLORS[item.priority],
            }}
          >
            {PRIORITY_LABELS[item.priority]}
          </Badge>
        </div>
        
        {/* Labels */}
        {item.labels && item.labels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.labels.slice(0, 3).map((label) => (
              <Badge
                key={label.id}
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: `${label.color}20`,
                  color: label.color,
                  borderColor: label.color,
                }}
              >
                {label.name}
              </Badge>
            ))}
            {item.labels.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{item.labels.length - 3} more
              </Badge>
            )}
          </div>
        )}
        
        {/* Assignee */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-3 w-3" />
          <span className="text-xs">
            {item.assignee ? item.assignee.full_name : 'Unassigned'}
          </span>
        </div>
        
            {/* Footer: Comments & Interaction */}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
              <div className="flex items-center gap-3">
                {item.comment_count > 0 && (
                  <div className="flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" />
                    <span>{item.comment_count}</span>
                  </div>
                )}
                {item.interaction && (
                  <LinkIcon className="h-3 w-3" />
                )}
              </div>
              <span>{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
